import { config } from '../config'

export interface MigrationDetails {
  username: string
  masked_email: string
  partner: string
}

export interface MigrationError {
  message: string
  shouldRetry: boolean
  redirectToHome: boolean
}

export interface ImportResponse {
  success: boolean
  redirect_url: string
}

/**
 * Get the display name for a partner
 */
export function getPartnerDisplayName(partnerId: string): string {
  const partnerConfig = config.partnerUrls[partnerId.toLowerCase() as keyof typeof config.partnerUrls]
  return partnerConfig?.displayName || partnerId
}

/**
 * Basic client-side validation of migration state parameter
 */
export function validateMigrationState(state: string | null): MigrationError | null {
  if (!state) {
    return {
      message: "This link doesn't look right. Please get a new link from SWGEmu.",
      shouldRetry: false,
      redirectToHome: true
    }
  }
  
  if (state.length < 10) {
    return {
      message: "This link doesn't look right. Please get a new link from SWGEmu.",
      shouldRetry: false,
      redirectToHome: true
    }
  }
  
  return null
}

/**
 * Fetch migration details for confirmation page
 */
export async function fetchMigrationDetails(state: string): Promise<MigrationDetails> {
  const response = await fetch(`${config.apiUrl}/auth/link/details?state=${encodeURIComponent(state)}`, {
    credentials: 'include',
    headers: {
      'Accept': 'application/json'
    }
  })
  
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('This migration link has expired. Please get a new link from SWGEmu.')
    } else if (response.status === 400) {
      throw new Error("This link isn't working. Please get a new link from SWGEmu.")
    } else if (response.status >= 500) {
      throw new Error('Something went wrong on our end. Please try again in a few minutes.')
    } else {
      throw new Error('Connection problem. Please check your internet and try again.')
    }
  }
  
  const data = await response.json()
  return data
}

/**
 * Execute the migration by creating the user account
 */
export async function executeMigration(partner: string, state: string): Promise<void> {
  const url = `${config.apiUrl}/auth/import?partner=${encodeURIComponent(partner)}&state=${encodeURIComponent(state)}`
  
  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include'
    })
    
    console.log('Migration response status:', response.status)
    
    // Handle successful response
    if (response.ok) {
      try {
        const data: ImportResponse = await response.json()
        console.log('Migration response data:', data)
        
        if (data.success && data.redirect_url) {
          console.log('Migration successful, redirecting to:', data.redirect_url)
          window.location.href = data.redirect_url
          return
        } else {
          throw new Error('Migration completed but response format was unexpected')
        }
      } catch (parseError) {
        console.log('Failed to parse success response:', parseError)
        throw new Error('Migration may have succeeded but response was unclear. Please check your account.')
      }
    }
    
    // Handle error responses (4xx, 5xx)
    let errorData: any = null
    let errorText = ''
    
    // Try to parse error response
    try {
      errorText = await response.text()
      console.log('Error response text:', errorText)
      errorData = JSON.parse(errorText)
      console.log('Parsed error data:', errorData)
    } catch (parseError) {
      console.log('Failed to parse error response:', parseError)
      // If we can't parse JSON, use the raw text or status-based handling
    }
    
    // Check for specific error messages first, regardless of status code
    if (errorData?.message) {
      if (errorData.message.includes('Missing validation cookie')) {
        throw new Error('This migration link has expired. Please get a new link from SWGEmu.')
      }
    }
    
    // Also check raw text for validation cookie errors
    if (errorText.includes('Missing validation cookie')) {
      throw new Error('This migration link has expired. Please get a new link from SWGEmu.')
    }
    
    // Fall back to status code based handling
    if (response.status === 401) {
      throw new Error('This migration link has expired. Please get a new link from SWGEmu.')
    } else if (response.status === 400) {
      throw new Error("This link isn't working properly. Please get a new one.")
    } else if (response.status === 409) {
      throw new Error('You already have a SWGRealms account with this email address. Try logging in instead.')
    } else if (response.status === 502) {
      // 502 Bad Gateway = Infrastructure problem, not user error
      throw new Error('Something went wrong on our end. Please contact support if this continues.')
    } else if (response.status >= 500) {
      throw new Error('Something went wrong on our end. Please try again in a few minutes.')
    } else {
      throw new Error('Connection problem. Please check your internet and try again.')
    }
    
  } catch (error) {
    console.log('Fetch error:', error)
    
    // Handle fetch failures (network errors, CORS, etc.)
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Connection problem. Please check your internet and try again.')
    }
    
    // Re-throw other errors (our custom error messages)
    throw error
  }
}

/**
 * Convert error types from URL parameters to user-friendly messages
 */
export function getErrorMessage(errorType: string | null): { message: string; canRetry: boolean; showLogin: boolean } {
  switch (errorType) {
    case 'expired_token':
      return {
        message: 'This link has expired. Please get a new link from SWGEmu.',
        canRetry: true, // Allow retry so they can go back to partner registration
        showLogin: false
      }
    case 'invalid_token':
      return {
        message: "This link isn't working. Please get a new link from SWGEmu.",
        canRetry: true, // Allow retry for invalid tokens too
        showLogin: false
      }
    case 'user_exists':
      return {
        message: 'You already have a SWGRealms account with this email address. Try logging in instead.',
        canRetry: false,
        showLogin: true
      }
    case 'email_invalid':
      return {
        message: "There's a problem with the email address. Please contact support@swgrealms.com",
        canRetry: false,
        showLogin: false
      }
    case 'server_error':
      return {
        message: 'Something went wrong on our end. Please try again in a few minutes.',
        canRetry: true,
        showLogin: false
      }
    default:
      return {
        message: 'Something unexpected happened. Please contact support@swgrealms.com if this continues.',
        canRetry: false,
        showLogin: false
      }
  }
}
